import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './DocumentsListView.css';
import '../list-view-page.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash, faRepeat, faChartBar, faTimesCircle, faClock, faScissors, faEye, faHistory } from '@fortawesome/free-solid-svg-icons';
import {
  CustomDropdown,
  MAIN_TAB_PAGE_LABEL,
  medPromptBrowserTitle,
  Table,
  ColumnCustomizationModal,
  ColumnCustomizationButton,
  RowContextMenu,
} from '@medprompt/shared';
import DocumentAssigneeModal from './DocumentAssigneeModal';
import useDocumentTable from './useDocumentTable';
import UploadQueueModal from './UploadQueueModal';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import ActivityModal from '../detail/tabs/ActivityModal';
import SearchBar from '../detail/tabs/SearchBar';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';
import DeleteDocumentModal from './DeleteDocumentModal';
import RegenerateDocumentModal from './RegenerateDocumentModal';
import BulkDeleteDocumentsModal from './BulkDeleteDocumentsModal';
import BulkRegenerateDocumentsModal from './BulkRegenerateDocumentsModal';
import BulkAssignDocumentsModal from './BulkAssignDocumentsModal';
import useDocumentList, { DocumentViewMode } from './useDocumentList';
import useRowPatientNames from '../patients/useRowPatientNames';
import { useDocumentUploadsContext } from './DocumentUploadsContext';
import useDocumentListDrop from './useDocumentListDrop';
import useDocumentRowActions, { getRowActions } from './useDocumentRowActions';
import '@medprompt/shared/src/common/modals.css';
import '@medprompt/shared/src/common/dropdowns.css';

const DocumentsListViewController = ({ viewerContext }) => {
  const navigate = useNavigate();
  const list = useDocumentList();
  const patientNames = useRowPatientNames({ service: list.documentService, idKey: 'documentId' });
  const uploads = useDocumentUploadsContext();
  const isDragging = useDocumentListDrop(uploads.enqueueFiles);
  const refreshDocumentsRef = useRef(list.refreshDocuments);
  refreshDocumentsRef.current = list.refreshDocuments;
  useEffect(() => {
    return uploads.registerRefreshDocuments(() => refreshDocumentsRef.current());
  }, [uploads.registerRefreshDocuments]);
  const rowActions = useDocumentRowActions({
    documents: list.visibleDocuments,
    documentService: list.documentService,
    refreshDocuments: list.refreshDocuments,
  });
  const table = useDocumentTable({ list, patientNames, rowActions, viewerContext });

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.documentsList);
  }, []);

  const handleViewModeChange = (option) => {
    list.selectViewMode(option);
    table.clearAssigneeFilter();
  };

  const contextRow = rowActions.rowContextMenu.row;

  return (
    <div className="documents-list-view list-view-page">
      <div className={`drop-zone ${isDragging ? 'active' : ''}`}>Drop file here</div>
      <div className="header">
        <div className="header-title-text">
          <div className="header-title-with-help">
            <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.documentsList}</h1>
            <HelpButton pageId={HELP_PAGE_IDS.documentsList} />
          </div>
          <CustomDropdown
            label="View Mode"
            selected={list.currentViewModeName}
            selectedIcon={list.currentViewModeIcon}
            options={list.viewModeOptions}
            isOpen={list.viewModeDropdown.isOpen}
            toggleDropdown={() => list.viewModeDropdown.toggle([])}
            onSelect={handleViewModeChange}
            containerRef={list.viewModeDropdown.dropdownRef}
            width="180px"
          />
        </div>
        <div className="header-buttons">
          {list.isSearchActive && (
            <button className="header-button clear-search-button" onClick={list.handleClearSearch}>
              <FontAwesomeIcon icon={faTimesCircle} /> Clear search
            </button>
          )}
          <SearchBar
            placeholder="Search for a patient's documents"
            onSearch={list.handleDocumentSearch}
            onResults={undefined} // Disable results callback to prevent dropdown (undefined is stable, unlike () => {})
            className="documents-list-search-bar"
            clearSearch={list.setClearSearchBarFunction}
          />
          <input
            type="file"
            id="file-upload"
            multiple
            accept=".pdf,.jpeg,.jpg,.png,.tif,.tiff,.doc,.docx,.rtf"
            style={{ display: 'none' }}
            onChange={uploads.handleFileUpload}
          />
          {list.hasDocsReadAll && (
            <button className="header-button" onClick={() => table.setAssigneeModalOpen(true)}>
              <FontAwesomeIcon icon={faChartBar} /> View assignee tally
            </button>
          )}
          <div style={{ position: 'relative', display: 'flex', gap: '5px', alignItems: 'center' }}>
            <button className="header-button header-button-primary" onClick={() => document.getElementById('file-upload').click()}>
              <FontAwesomeIcon icon={faUpload} /> Upload document
            </button>

            <button
              className="header-button upload-queue-toggle-button"
              onClick={() => navigate('/split-pdf')}
              title="Split a PDF into multiple documents"
            >
              <FontAwesomeIcon icon={faScissors} />
            </button>

            <button
              className="header-button upload-queue-toggle-button"
              onClick={uploads.openUploadQueue}
              style={{ position: 'relative' }}
            >
              <FontAwesomeIcon icon={faClock} />
              {uploads.pendingUploadCount > 0 && (
                <span className="upload-badge">
                  {uploads.pendingUploadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {list.isSearchActive && (
        <div className="documents-search-results-info">
          Found {list.searchResults.length} document{list.searchResults.length !== 1 ? 's' : ''} matching "{list.searchQuery}"
        </div>
      )}

      <Table
        id="DocumentsListTable"
        data={list.visibleDocuments}
        columns={table.columns}
        columnOrder={table.columnOrder}
        hiddenColumns={table.hiddenColumnsSet}
        settingsButton={<ColumnCustomizationButton onClick={() => table.setColumnCustomizationOpen(true)} />}
        getRowKey={(doc) => doc.documentId}
        onRowContextMenu={rowActions.handleRowContextMenu}
        sortState={table.tableSortState}
        onSortChange={table.setTableSortState}
        filterValues={table.tableFilterValues}
        onFilterChange={table.setTableFilterValues}
        disabledFilters={table.tableDisabledFilters}
        onDisabledFiltersChange={table.setTableDisabledFilters}
        paginationState={table.tablePaginationState}
        onPaginationChange={table.setTablePaginationState}
        ensureRowVisibleKey={table.ensureRowVisibleKey}
        onCurrentPageRowsChange={patientNames.handleCurrentPageRowsChange}
        onFilteredSortedDataChange={table.handleFilteredSortedDataChange}
        onRowClick={table.openDocument}
        enableSelection={true}
        bulkActions={table.bulkActions}
        selectedRows={table.selectedRows}
        onSelectionChange={table.setSelectedRows}
        highlightedRows={table.highlightedRows}
        onHighlightChange={table.setHighlightedRows}
        emptyMessage={
          <div className="no-documents-container">
            {list.viewMode === DocumentViewMode.PENDING_DOCUMENTS
              ? 'No documents pending processing'
              : list.hasDocsReadAll
                ? 'No documents found'
                : 'No documents assigned to you were found'
            }
          </div>
        }
      />

      <DeleteDocumentModal
        isOpen={rowActions.deleteModalOpen}
        onClose={rowActions.closeDeleteModal}
        onConfirm={rowActions.confirmDelete}
        document={rowActions.documentToDelete}
        getAssigneeName={list.getAssigneeName}
      />

      <RegenerateDocumentModal
        isOpen={rowActions.regenerateModalOpen}
        onClose={rowActions.closeRegenerateModal}
        onConfirm={rowActions.confirmRegenerate}
        document={rowActions.documentToRegenerate}
        loading={rowActions.regenerateLoading}
        getAssigneeName={list.getAssigneeName}
      />

      <BulkDeleteDocumentsModal
        isOpen={table.bulkDeleteModalOpen}
        onClose={table.closeBulkModal}
        onConfirm={table.confirmBulkDelete}
        documents={table.documentsForBulkAction}
        getAssigneeName={list.getAssigneeName}
      />

      <BulkRegenerateDocumentsModal
        isOpen={table.bulkRegenerateModalOpen}
        onClose={table.closeBulkModal}
        onConfirm={table.confirmBulkRegenerate}
        documents={table.documentsForBulkAction}
        loading={table.bulkRegenerateLoading}
        getAssigneeName={list.getAssigneeName}
      />

      <BulkAssignDocumentsModal
        isOpen={table.bulkAssignModalOpen}
        onClose={table.closeBulkModal}
        onConfirm={table.confirmBulkAssign}
        documents={table.documentsForBulkAction}
        activeUsers={list.activeUsers}
        teams={list.teams}
        loading={table.bulkAssignLoading}
        getAssigneeName={list.getAssigneeName}
      />

      <DocumentAssigneeModal
        isOpen={table.assigneeModalOpen}
        onClose={() => table.setAssigneeModalOpen(false)}
        activeUsers={list.activeUsers}
        teams={list.teams}
        onAssigneeClick={table.handleAssigneeFilterClick}
      />

      <UploadQueueModal
        isOpen={uploads.uploadQueueModalOpen}
        onClose={uploads.closeUploadQueue}
        uploadQueue={uploads.uploadQueue}
        onClearCompleted={uploads.clearCompletedUploads}
        onConfirmDuplicate={uploads.confirmDuplicateUpload}
        onClearHistory={uploads.clearUploadHistory}
      />

      {/* Document preview modal (from the Actions column or row right-click) */}
      <DocumentPreviewModal
        isOpen={!!rowActions.previewDocumentId}
        onClose={rowActions.closePreview}
        documentId={rowActions.previewDocumentId}
        activeUsers={list.activeUsers}
        teams={list.teams}
      />

      {rowActions.auditDocument && (
        <ActivityModal
          doc={rowActions.auditDocument}
          documentId={rowActions.auditDocument.documentId}
          activeUsers={list.activeUsers}
          teams={list.teams}
          onClose={rowActions.closeAuditTrail}
        />
      )}

      <RowContextMenu
        open={rowActions.rowContextMenu.open}
        x={rowActions.rowContextMenu.x}
        y={rowActions.rowContextMenu.y}
        onClose={rowActions.closeRowContextMenu}
        items={!contextRow ? [] : [
          {
            key: 'preview',
            icon: faEye,
            label: 'Preview document',
            onClick: () => rowActions.openPreview(contextRow.documentId),
          },
          {
            key: 'audit',
            icon: faHistory,
            label: 'View audit trail',
            onClick: () => rowActions.openAuditTrail(contextRow),
          },
          {
            key: 'split',
            icon: faScissors,
            label: 'Split into multiple documents',
            onClick: () => rowActions.handleSplitExistingDocument(contextRow),
            hidden: !getRowActions(contextRow).canSplit,
          },
          {
            key: 'regenerate',
            icon: faRepeat,
            label: 'Re-process document',
            onClick: () => rowActions.handleRegenerateClick(contextRow.documentId),
            hidden: !getRowActions(contextRow).canRegenerate,
          },
          {
            key: 'delete',
            icon: faTrash,
            label: 'Delete document',
            onClick: () => rowActions.handleDeleteClick(contextRow.documentId),
            hidden: !getRowActions(contextRow).canDelete,
            danger: true,
          },
        ]}
      />

      <ColumnCustomizationModal
        isOpen={table.columnCustomizationOpen}
        onClose={() => table.setColumnCustomizationOpen(false)}
        columns={table.columns}
        currentOrder={table.columnOrder}
        hiddenColumns={table.hiddenColumnsSet}
        defaultHidden={table.defaultHidden}
        onSave={(order, hidden) => {
          table.saveSettings(order, Array.from(hidden));
          table.setColumnCustomizationOpen(false);
        }}
      />
    </div>
  );
};

export default DocumentsListViewController;
